import { Typography, Box } from '@mui/material';
import React from 'react';  
import { styled } from '@mui/material/styles';
import { EmailSection } from '../../../types';

// Color mapper for different variants
const variantColorMap: Record<string, { primary: string; secondary: string }> = {
  purple: {
    primary: '#8b5cf6',
    secondary: '#7c3aed'
  },
  blue: {
    primary: '#3b82f6',
    secondary: '#2563eb'
  },
  green: {
    primary: '#10b981',
    secondary: '#059669'
  },
  red: {
    primary: '#ef4444',
    secondary: '#dc2626'
  },
  orange: {
    primary: '#f97316',
    secondary: '#ea580c'
  },
  teal: {
    primary: '#14b8a6',
    secondary: '#0d9488'
  },
  indigo: {
    primary: '#6366f1',
    secondary: '#4f46e5'
  },
  pink: {
    primary: '#ec4899',
    secondary: '#db2777'
  },
  default: {
    primary: '#6b7280',
    secondary: '#4b5563'
  }
};

const HighlightSection = styled(Box)<{ color: string }>(({ theme, color }) => ({
  borderLeft: '4px solid',
  borderColor: color,
  backgroundColor: `${color}08`,
  padding: theme.spacing(4),
  borderRadius: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px 0`,
  margin: theme.spacing(2, 0),
}));
  
const HighlightTitle = styled(Box)<{ color: string }>(({ theme, color }) => ({
  fontWeight: 'bold',
  color: color,
  marginBottom: theme.spacing(1),
}));

interface EmailContentSectionProps {
  variant: string;
  emailSection: EmailSection;
}

export const EmailContentSection: React.FC<EmailContentSectionProps> = ({ 
  variant = "default",
  emailSection
}) => {
  // Get colors based on variant

  if(variant === 'default') {
    return (
      <Box 
        data-section-id={emailSection.id}
        data-section-variant={variant}
        sx={{ 
          '& p': { margin: '8px 0' },
          '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
          '& li': { margin: '4px 0' }
        }}
      >
        <Typography 
          dangerouslySetInnerHTML={{ __html: emailSection.content || '' }}
        />
        {emailSection.img_url && (
          <Box sx={{ marginTop: 2, textAlign: 'center' }}>
            <img 
              src={emailSection.img_url} 
              alt={emailSection.title || 'Email content image'}
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box 
      data-section-id={emailSection.id}
      data-section-variant={variant}
    >
      <HighlightSection color={variantColorMap[variant].primary}>
        <HighlightTitle color={variantColorMap[variant].secondary}>{emailSection.title}</HighlightTitle>
        <Typography 
          dangerouslySetInnerHTML={{ __html: emailSection.content || '' }}
          sx={{ 
            '& p': { margin: '8px 0' },
            '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
            '& li': { margin: '4px 0' }
          }}
        />
        {emailSection.img_url && (
          <Box sx={{ marginTop: 2, textAlign: 'center' }}>
            <img 
              src={emailSection.img_url} 
              alt={emailSection.title || 'Email content image'}
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </Box>
        )}
      </HighlightSection>
    </Box>
  );
};
